import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import CTAButton from "../shared/CTAButton";
import LangLink from "../shared/LangLink";
import { useI18n } from "../../i18n";
import { DISCLAIMER_SHORT } from "../../data/site";

const EASE = [0.76, 0, 0.24, 1];

const MaskLine = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-background" data-testid="hero-section">
      <div className="container-psv pt-16 md:pt-24 lg:pt-28 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Text column */}
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <span className="eyebrow" data-testid="hero-eyebrow">{t("hero.trust")[2]}</span>
            </motion.div>

            <h1 className="mt-6 font-serif font-medium text-primary text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.95] tracking-tight" data-testid="hero-heading">
              <MaskLine delay={0.15}>{t("hero.line1")}</MaskLine>
              <MaskLine delay={0.28}><span className="italic text-accent">{t("hero.line2")}</span></MaskLine>
              <MaskLine delay={0.41}>{t("hero.line3")}</MaskLine>
            </h1>

            <motion.p
              className="mt-8 text-lg md:text-xl text-secondary-foreground font-light max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
              data-testid="hero-subheading"
            >
              {t("hero.subheading")}
            </motion.p>

            <motion.p
              className="mt-5 text-[15px] text-muted-foreground max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.82, duration: 0.8 }}
            >
              {t("hero.paragraph")}
            </motion.p>

            <motion.div
              className="mt-9 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }}
            >
              <CTAButton to="/#case-review" size="lg" testId="hero-case-review-btn">{t("cta.caseReview")}</CTAButton>
              <CTAButton to="/procedures" size="lg" variant="outline" testId="hero-compare-btn">{t("cta.compare")}</CTAButton>
              <LangLink to="/procedures" className="text-sm text-primary/70 hover:text-accent underline underline-offset-4 font-sans" data-testid="hero-explore-link">
                {t("cta.explore")}
              </LangLink>
            </motion.div>

            <motion.ul
              className="mt-8 flex flex-wrap gap-x-8 gap-y-2"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
            >
              {t("hero.trust").map((tp, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-primary/80 font-sans">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" /> {tp}
                </li>
              ))}
            </motion.ul>
          </div>

          {/* Image column — clipped frame with parallax */}
          <div className="lg:col-span-5">
            <motion.div
              className="relative overflow-hidden rounded-[2rem] grain h-[420px] sm:h-[520px] lg:h-[640px] border border-border"
              initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 1, ease: EASE }}
              data-testid="hero-image"
            >
              <motion.img
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="A calm consultation between an international patient and a healthcare professional"
                className="absolute inset-0 w-full h-[118%] object-cover"
                style={{ y: imgY }}
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/25 to-transparent pointer-events-none" />
            </motion.div>
          </div>
        </div>

        {/* Disclaimer + scroll cue */}
        <motion.div
          className="mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-t border-border pt-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }}
        >
          <p className="text-[13px] text-muted-foreground max-w-2xl" data-testid="hero-disclaimer">{DISCLAIMER_SHORT}</p>
          <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <ArrowDown className="w-4 h-4 animate-bounce" /> {t("hero.scroll")}
          </span>
        </motion.div>
      </div>
    </section>
  );
}
